import React from "react"
import {useAsyncResult} from "../../../hooks/useAsyncResult"
import {CardTotals} from "../../../resources/account"
import {AsyncResultComponent} from "../../../containers/AsyncResult/AsyncResult"
import RangeBox from "../../RangeBox/RangeBox"
import Icon from "../../Icon/Icon"
import {IcomoonIconName} from "../../Icon/icons"
import {faQuestionCircle} from "@fortawesome/free-regular-svg-icons"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import ReactTooltip from "react-tooltip"
import BodyPortal from "../../BodyPortal/BodyPortal"
import Skeleton from "react-loading-skeleton"
import {Card, getCardLimits} from "../../../resources/card"
import Admonition from "../../Admonitions/Admonition"

function CardLimitsBlockHeader({title, icon, infoTip = ""}: {title: string; icon: IcomoonIconName; infoTip?: string}) {
	return (
		<div className="limits-drawer-block-header">
			<div className="limits-drawer-block-header-icon-placeholder">
				<Icon icon={icon} size={20} />
			</div>
			<h4> {title} </h4>
			{infoTip ? (
				<div
					className="limits-drawer-block-header-info-icon-placeholder"
					data-tip={infoTip}
					data-iscapture={true}
					data-for={"limits-drawer-info-icon"}
				>
					<FontAwesomeIcon icon={faQuestionCircle} />
					<BodyPortal>
						<ReactTooltip
							arrowColor="#FFF"
							delayHide={300}
							className="limits-drawer-info-tooltip"
							place="right"
							type="light"
							effect="solid"
							id={"limits-drawer-info-icon"}
						/>
					</BodyPortal>
				</div>
			) : (
				""
			)}
		</div>
	)
}

export function CardLimitsDescription({cardLast4Digits, accountId}: {cardLast4Digits: string; accountId: string}) {
	return (
		<>
			The limits below are the card-level limits for the card ••{cardLast4Digits} and might differ from the account
			limits (••{accountId.slice(-4)}).
		</>
	)
}

function CardLimitsDescriptionWithLink({cardLast4Digits, accountId}: {cardLast4Digits: string; accountId: string}) {
	return (
		<p className="limits-drawer-description">
			<CardLimitsDescription cardLast4Digits={cardLast4Digits} accountId={accountId} />
		</p>
	)
}

function CardLimitsPending() {
	return (
		<div className="limits-drawer">
			<CardLimitsBlockHeader title="Card Limits" icon="credit-card-money-fund--business-products" />
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	)
}

function CardLimitRanges({
	cardLevelLimits,
	cardTotalsDaily,
	cardTotalsMonthly,
}: {
	cardLevelLimits: {
		dailyWithdrawal: number
		dailyPurchase: number
		monthlyWithdrawal: number
		monthlyPurchase: number
		dailyCardTransaction?: number
		monthlyCardTransaction?: number
	}
	cardTotalsDaily: CardTotals
	cardTotalsMonthly: CardTotals
}) {
	return (
		<>
			<CardLimitsBlockHeader
				title="Card Limits"
				icon="credit-card-money-fund--business-products"
				infoTip="This limit is based on the limit set in the card limits"
			/>

			<div className="limits-drawer-block-limits">
				{cardLevelLimits.dailyPurchase != null && (
					<RangeBox
						title="Purchases"
						description="(Daily Limit)"
						className="limits-drawer-range-box"
						max={cardLevelLimits.dailyPurchase}
						current={cardTotalsDaily.purchases}
					/>
				)}

				{cardLevelLimits.monthlyPurchase != null && (
					<RangeBox
						title="Purchases"
						description="(Monthly Limit)"
						className="limits-drawer-range-box"
						max={cardLevelLimits.monthlyPurchase}
						current={cardTotalsMonthly.purchases}
					/>
				)}

				{cardLevelLimits.dailyWithdrawal != null && (
					<RangeBox
						title="ATM Withdrawal"
						description="(Daily Limit)"
						className="limits-drawer-range-box"
						max={cardLevelLimits.dailyWithdrawal}
						current={cardTotalsDaily.withdrawals}
					/>
				)}

				{cardLevelLimits.monthlyWithdrawal != null && (
					<RangeBox
						title="ATM Withdrawal"
						description="(Monthly Limit)"
						className="limits-drawer-range-box"
						max={cardLevelLimits.monthlyWithdrawal}
						current={cardTotalsMonthly.withdrawals}
					/>
				)}

				{cardLevelLimits.dailyCardTransaction != null && (
					<RangeBox
						title="Transaction"
						description="(Daily Limit)"
						className="limits-drawer-range-box"
						max={cardLevelLimits.dailyCardTransaction}
						current={cardTotalsDaily.cardTransactions}
					/>
				)}

				{cardLevelLimits.monthlyCardTransaction != null && (
					<RangeBox
						title="Transaction"
						description="(Monthly Limit)"
						className="limits-drawer-range-box"
						max={cardLevelLimits.monthlyCardTransaction}
						current={cardTotalsMonthly.cardTransactions}
					/>
				)}
			</div>
		</>
	)
}

export default function CardLimits({
	accountId,
	card,
	accessToken,
}: {
	accountId: string
	card: Card
	accessToken: string
}) {
	const cardLimits = useAsyncResult(() => getCardLimits(accessToken, card.id))

	return (
		<AsyncResultComponent asyncResult={cardLimits} pendingComponent={<CardLimitsPending />}>
			{({value: cardLimits}) => {
				return (
					<div className="limits-drawer">
						<CardLimitsDescriptionWithLink accountId={accountId} cardLast4Digits={card.attributes.last4Digits} />

						{cardLimits.attributes.limits && Object.keys(cardLimits.attributes.limits).length > 0 ? (
							<CardLimitRanges
								cardLevelLimits={cardLimits.attributes.limits}
								cardTotalsDaily={cardLimits.attributes.dailyTotals}
								cardTotalsMonthly={cardLimits.attributes.monthlyTotals}
							/>
						) : (
							<Admonition
								type={"is-info"}
								title={"No card-level limits set for this card"}
								message={"Please review account level limits for account level card limits"}
							/>
						)}
					</div>
				)
			}}
		</AsyncResultComponent>
	)
}
